// Deterministic color-by-connector for workflow step nodes.
//
// Each connector gets a stable, DISTINCT color. Colors are assigned by the
// connector's position in the runbook's connector list (so two connectors that
// happen to share a system type — e.g. two Intacct connectors — still get
// different colors). Steps that don't reference a connector (If/Else, For Each,
// Set Var, Log, etc.) render grey.
//
// NOTE: we key on the connector IDENTITY (connectorId / connectorNumber) only —
// NOT `system`. Keying on `system` collapses distinct connectors that share a
// type into one color, which is the bug this replaces.
//
// Colorblind-safe hues that read on both light and dark canvases. Red is
// intentionally excluded — it's reserved for the error handle/edge.
export const PALETTE = [
  '#2563eb', // blue
  '#0d9488', // teal
  '#7c3aed', // violet
  '#d97706', // amber
  '#db2777', // magenta
  '#059669', // green
  '#0891b2', // cyan
  '#4f46e5', // indigo
  '#ca8a04', // dark yellow
  '#9333ea', // purple
];

// Neutral accent for non-connector (logic/variable) steps on the CANVAS. Their
// box renders white (see .is-generic in StepNode.css); this Armanino brand teal
// is used for the icon, verb and left bar. (The left step-palette uses its own
// neutral — black — set in StepPalette.jsx; the two surfaces are independent.)
export const NEUTRAL_ACCENT = '#007681';

// Armanino brand purple -- also used by WorkflowBuilder for edges that jump straight to an
// End Loop step (#2662), so the two stay in sync.
export const BRAND_PURPLE = '#6A3460';

// Per-step-type accent overrides for non-connector steps, so loop/branch
// structure stands out from the neutral teal.
const STEP_ACCENT_OVERRIDES = {
  ifElse: BRAND_PURPLE,
  forEach: '#949300',  // olive (rgb 148,147,0)
  endLoop: '#949300',
};

// The connector identity we color by. Anything falsy => not a connector step.
export function connectorKey(data) {
  if (!data) return null;
  return data.connectorId || data.connectorNumber || null;
}

export function isConnectorStep(data) {
  return connectorKey(data) != null;
}

// Build a stable identity -> color map from the runbook's connector list.
// Keyed by BOTH connectorId and connectorNumber so a node resolves regardless
// of which field it carries. Index-based => guaranteed distinct per connector.
export function buildConnectorColorMap(connectors) {
  const map = {};
  (connectors || []).forEach((c, i) => {
    if (!c) return;
    const color = PALETTE[i % PALETTE.length];
    if (c.connectorId) map[c.connectorId] = color;
    if (c.connectorNumber) map[c.connectorNumber] = color;
  });
  return map;
}

// Stable string hash (FNV-1a) — fallback when a connector identity exists but
// isn't in the runbook list (shouldn't normally happen).
function hashString(str) {
  let hash = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

// Resolve a node's accent color. `colorMap` is the map from
// buildConnectorColorMap (the connector list). Grey when the step has no
// connector identity.
export function colorForNode(data, colorMap) {
  const key = connectorKey(data);
  if (!key) {
    return STEP_ACCENT_OVERRIDES[data?.stepType] || NEUTRAL_ACCENT;
  }
  if (colorMap && colorMap[key]) return colorMap[key];
  return PALETTE[hashString(String(key)) % PALETTE.length];
}
